/**
 * Best-effort browser name + version + device hints for audit / timeline logs.
 * Uses User-Agent Client Hints (navigator.userAgentData) when available, else UA string.
 */

function pickBrand(brands) {
    if (!brands?.length) return null;
    const list = brands.filter((b) => b?.brand && !/Not\.?A\.?Brand/i.test(b.brand));
    const edge = list.find((b) => /Edge/i.test(b.brand));
    if (edge) return edge;
    const opera = list.find((b) => /Opera|OPR/i.test(b.brand));
    if (opera) return opera;
    const samsung = list.find((b) => /SamsungBrowser/i.test(b.brand));
    if (samsung) return samsung;
    const chrome = list.find((b) => /Google Chrome/i.test(b.brand));
    if (chrome) return chrome;
    const nonChromium = list.find((b) => !/^Chromium$/i.test(b.brand));
    return nonChromium || list[list.length - 1];
}

function parseFromUserAgent(ua) {
    if (!ua) {
        return { browserType: 'unknown', browserVersion: null, deviceModel: null, deviceClass: null };
    }
    let browserType = 'unknown';
    let browserVersion = null;

    if (/Edg(?:e|A|iOS)?\/(\d+)/i.test(ua)) {
        browserType = 'Microsoft Edge';
        browserVersion = RegExp.$1;
    } else if (/CriOS\/(\d+)/i.test(ua)) {
        browserType = 'Chrome';
        browserVersion = RegExp.$1;
    } else if (/FxiOS\/(\d+)/i.test(ua)) {
        browserType = 'Firefox';
        browserVersion = RegExp.$1;
    } else if (/OPR\/(\d+)/i.test(ua)) {
        browserType = 'Opera';
        browserVersion = RegExp.$1;
    } else if (/SamsungBrowser\/(\d+)/i.test(ua)) {
        browserType = 'Samsung Internet';
        browserVersion = RegExp.$1;
    } else if (/Chrome\/(\d+)/i.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua)) {
        browserType = 'Chrome';
        browserVersion = RegExp.$1;
    } else if (/Firefox\/(\d+)/i.test(ua)) {
        browserType = 'Firefox';
        browserVersion = RegExp.$1;
    } else if (/Version\/(\d+)[\d.]*.*Safari/i.test(ua) && /Safari/i.test(ua) && !/Chrome|Chromium|Edg|Android/i.test(ua)) {
        browserType = 'Safari';
        browserVersion = RegExp.$1;
    } else if (/Safari/i.test(ua) && /Mobile/i.test(ua)) {
        browserType = 'Safari';
        const m = ua.match(/Version\/(\d+)/i);
        browserVersion = m ? m[1] : null;
    }

    let deviceClass = null;
    if (/iPhone/i.test(ua)) deviceClass = 'iPhone';
    else if (/iPad/i.test(ua)) deviceClass = 'iPad';
    else if (/Android/i.test(ua)) deviceClass = 'Android';

    let deviceModel = null;
    const android = ua.match(/Android\s[\d.]+;\s*([^)]+)\)/i);
    if (android) {
        deviceModel = android[1].trim().split(/\s+Build\b/i)[0].trim();
    }

    return { browserType, browserVersion, deviceModel, deviceClass };
}

/**
 * @returns {{ browserType: string, browserVersion: string | null, deviceModel: string | null, deviceClass: string | null, mobile?: boolean }}
 */
export function getClientBrowserInfo() {
    if (typeof navigator === 'undefined') {
        return { browserType: 'unknown', browserVersion: null, deviceModel: null, deviceClass: null };
    }

    const ua = navigator.userAgent || '';
    const uad = navigator.userAgentData;
    const fromUa = parseFromUserAgent(ua);

    if (uad?.brands?.length) {
        const b = pickBrand(uad.brands);
        if (b) {
            return {
                browserType: b.brand,
                browserVersion: b.version || null,
                deviceModel: fromUa.deviceModel,
                deviceClass: fromUa.deviceClass,
                mobile: typeof uad.mobile === 'boolean' ? uad.mobile : undefined,
            };
        }
    }

    return {
        ...fromUa,
        mobile: typeof uad?.mobile === 'boolean' ? uad.mobile : undefined,
    };
}

/** Subset sent with timeline POSTs (keeps body small). */
export function getTimelineBrowserFields() {
    const o = getClientBrowserInfo();
    const out = {
        browserType: o.browserType,
        browserVersion: o.browserVersion,
    };
    if (o.deviceModel) out.deviceModel = o.deviceModel;
    if (o.deviceClass) out.deviceClass = o.deviceClass;
    if (typeof o.mobile === 'boolean') out.mobile = o.mobile;
    return out;
}
